import { FormattedMessage } from "react-intl";

import type { TranslationID } from "@citrine/lang/utils";
import type { BranchNodeType } from "@citrine/resources/branch";

import { NodeIcon } from "#src/components/node-icon";

import * as sx from "./branch-step.sx";

export interface BranchStepProps {
  nodeType: BranchNodeType;
  complete?: boolean;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  to?: string;
  version?: number;
}

type BranchSteps = { [K in BranchNodeType]: TranslationID };

const BRANCH_STEPS: BranchSteps = {
  data: "resources.branch-navigation.step-placeholder.training-set",
  predictor: "resources.branch-navigation.step-placeholder.predictor",
  design: "resources.branch-navigation.step-placeholder.design",
  candidate: "resources.branch-navigation.step-placeholder.candidate",
};

export function BranchStep({
  nodeType,
  complete,
  label,
  to,
  tooltip,
  version,
}: BranchStepProps) {
  const content = (
    <>
      {label ? (
        <sx.TitleContainer>
          <NodeIcon type={nodeType} fontSize="small" />
          <sx.Tooltip
            fullWidth
            title={
              tooltip || (
                <FormattedMessage
                  id="resources.branch-navigation.version"
                  values={{
                    p: (chunks) => <span>{chunks}</span>,
                    prefix: label,
                    v: (chunks) => <span>{chunks}</span>,
                    version: version || "_",
                  }}
                />
              )
            }
          >
            <sx.LabelContainer>
              <FormattedMessage
                id="resources.branch-navigation.version"
                values={{
                  p: (chunks) => <sx.Label variant="body1">{chunks}</sx.Label>,
                  prefix: label,
                  v: (chunks) => (
                    <sx.Version variant="body1">{chunks}</sx.Version>
                  ),
                  version: version || "_",
                }}
              />
            </sx.LabelContainer>
          </sx.Tooltip>
        </sx.TitleContainer>
      ) : (
        <sx.TitleContainer>
          <NodeIcon type={nodeType} fontSize="small" />
          <sx.Title variant="body1">
            <FormattedMessage id={BRANCH_STEPS[nodeType]} />
          </sx.Title>
        </sx.TitleContainer>
      )}
      <sx.Indicator />
    </>
  );

  return to ? (
    <sx.StepLink
      data-testid={`${nodeType}-step`}
      complete={complete}
      nodeType={nodeType}
      to={to}
    >
      {content}
    </sx.StepLink>
  ) : (
    <sx.Step
      data-testid={`${nodeType}-step`}
      complete={complete}
      nodeType={nodeType}
    >
      {content}
    </sx.Step>
  );
}
